const GUIDANCE_LEVELS=new Set(['low','medium','high']);
const CONFIDENCE_LEVELS=new Set(['low','medium','high']);

export const ProductCategory=Object.freeze({
  PORK:'pork',
  BEEF:'beef',
  POULTRY:'poultry',
  GAME:'game',
  FISH:'fish',
  CHEESE:'cheese',
  VEGETABLES:'vegetables'
});

export const GuidanceConfidence=Object.freeze({
  LOW:'low',
  MEDIUM:'medium',
  HIGH:'high'
});

export const WOOD_PRODUCT_GUIDANCE_DISCLAIMER=Object.freeze({
  pl:'To wskazówka smakowa, nie bezwzględna reguła ani zalecenie dotyczące bezpieczeństwa żywności.',
  en:'This is flavour guidance, not an absolute rule or a food-safety recommendation.'
});

const PRODUCT_KEYS=new Set(Object.values(ProductCategory));
const GUIDANCE_RANK=Object.freeze({low:1,medium:2,high:3});
const CONFIDENCE_RANK=Object.freeze({low:1,medium:2,high:3});

function key(value,label,{controlled=null}={}){
  const result=String(value||'').trim().toLowerCase();
  if(!/^[a-z][a-z0-9_]{1,63}$/.test(result))throw new TypeError(`Invalid ${label}`);
  if(controlled&&!controlled.has(result))throw new TypeError(`Unsupported ${label}`);
  return result;
}

function requiredText(value,label,max=500){
  const text=String(value||'').trim();
  if(!text||text.length>max)throw new TypeError(`${label} is invalid`);
  return text;
}

function level(value,label,allowed){
  const result=String(value||'').trim().toLowerCase();
  if(!allowed.has(result))throw new TypeError(`Unsupported ${label}`);
  return result;
}

function version(value,label){
  const number=Number(value);
  if(!Number.isInteger(number)||number<1)throw new TypeError(`${label} must be a positive integer`);
  return number;
}

function localeKey(locale){
  return String(locale||'pl').toLowerCase().startsWith('en')?'en':'pl';
}

export function createWoodProductGuidance({
  woodKey,
  productKey,
  guidanceLevel,
  confidenceLevel,
  rationalePl,
  rationaleEn,
  sourceName,
  sourceVersion,
  guidanceVersion=1
}={}){
  return Object.freeze({
    woodKey:key(woodKey,'woodKey'),
    productKey:key(productKey,'productKey',{controlled:PRODUCT_KEYS}),
    guidanceLevel:level(guidanceLevel,'guidanceLevel',GUIDANCE_LEVELS),
    confidenceLevel:level(confidenceLevel,'confidenceLevel',CONFIDENCE_LEVELS),
    rationales:Object.freeze({
      pl:requiredText(rationalePl,'rationalePl'),
      en:requiredText(rationaleEn,'rationaleEn')
    }),
    sourceName:requiredText(sourceName,'sourceName',160),
    sourceVersion:requiredText(sourceVersion,'sourceVersion',80),
    guidanceVersion:version(guidanceVersion,'guidanceVersion'),
    isGuidance:true,
    guidanceOnly:true,
    disclaimerKey:'subjective_pairing_guidance'
  });
}

export function woodProductGuidanceFromRow(row={}, {locale='pl'}={}){
  const guidance=createWoodProductGuidance({
    woodKey:row.wood_key,
    productKey:row.product_key,
    guidanceLevel:row.guidance_level,
    confidenceLevel:row.confidence_level,
    rationalePl:row.rationale_pl,
    rationaleEn:row.rationale_en,
    sourceName:row.source_name,
    sourceVersion:row.source_version,
    guidanceVersion:row.guidance_version
  });
  const lang=localeKey(locale);
  return Object.freeze({
    ...guidance,
    rationale:guidance.rationales[lang],
    disclaimer:WOOD_PRODUCT_GUIDANCE_DISCLAIMER[lang]
  });
}

export function guidanceCompositeKey(value={}){
  const woodKey=key(value.woodKey,'woodKey');
  const productKey=key(value.productKey,'productKey',{controlled:PRODUCT_KEYS});
  const guidanceVersion=version(value.guidanceVersion??1,'guidanceVersion');
  return `${woodKey}:${productKey}:v${guidanceVersion}`;
}

export function rankWoodGuidance(items=[]){
  if(!Array.isArray(items))throw new TypeError('guidance items must be an array');
  const checked=items.map(item=>{
    if(!item||typeof item!=='object')throw new TypeError('guidance item must be an object');
    key(item.woodKey,'woodKey');
    key(item.productKey,'productKey',{controlled:PRODUCT_KEYS});
    level(item.guidanceLevel,'guidanceLevel',GUIDANCE_LEVELS);
    level(item.confidenceLevel,'confidenceLevel',CONFIDENCE_LEVELS);
    return item;
  });
  return Object.freeze([...checked].sort((a,b)=>
    GUIDANCE_RANK[b.guidanceLevel]-GUIDANCE_RANK[a.guidanceLevel]
    ||CONFIDENCE_RANK[b.confidenceLevel]-CONFIDENCE_RANK[a.confidenceLevel]
    ||String(a.woodKey).localeCompare(String(b.woodKey))
  ));
}
